import { useMemo, useState } from 'react'

import { formatNumber } from '@/lib/format'
import { columnPath, niceTicks } from '@/components/charts/shapes'
import { ChartFrame, type ChartDatum } from '@/components/charts/ChartFrame'

export interface ColumnChartProps {
  title: string
  subtitle?: string
  data: ChartDatum[]
  categoryHeading?: string
  valueHeading?: string
  className?: string
}

const WIDTH = 1100
const HEIGHT = 118
const PADDING = { top: 14, right: 6, bottom: 18, left: 34 }
const SURFACE_GAP = 2 // the spec's gap between adjacent marks
const MAX_BAR = 14

/**
 * Columns over a time axis: one series, one colour, no legend.
 *
 * Only the peak is direct-labelled: a value on every column is unreadable, so
 * the axis ticks and the hover tooltip carry the rest.
 */
export function ColumnChart({
  title,
  subtitle,
  data,
  categoryHeading = 'Year',
  valueHeading,
  className,
}: ColumnChartProps) {
  const [hovered, setHovered] = useState<number | null>(null)

  const plotWidth = WIDTH - PADDING.left - PADDING.right
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom
  const baselineY = PADDING.top + plotHeight

  const { max, ticks, peakIndex } = useMemo(() => {
    const highest = Math.max(...data.map((datum) => datum.value), 1)
    const tickValues = niceTicks(highest, 2)
    return {
      max: Math.max(highest, tickValues[tickValues.length - 1] ?? highest),
      ticks: tickValues,
      peakIndex: data.findIndex((datum) => datum.value === highest),
    }
  }, [data])

  if (data.length === 0) {
    return (
      <ChartFrame title={title} subtitle={subtitle} data={data} className={className}>
        <p className="py-6 text-center text-xs text-ink-muted">No data yet.</p>
      </ChartFrame>
    )
  }

  const band = plotWidth / data.length
  const barWidth = Math.min(Math.max(band - SURFACE_GAP, 1), MAX_BAR)
  // Label every nth year so the axis never collides with itself.
  const labelEvery = Math.ceil(data.length / 12)

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      data={data}
      categoryHeading={categoryHeading}
      valueHeading={valueHeading}
      className={className}
    >
      <div className="relative">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="w-full"
          role="img"
          aria-label={`${title}. ${data.length} periods; the table below lists every value.`}
          onMouseLeave={() => setHovered(null)}
        >
          {/* Gridlines: hairline, solid, one step off the surface. */}
          {ticks.map((tick) => {
            const y = baselineY - (tick / max) * plotHeight
            return (
              <g key={tick}>
                <line
                  x1={PADDING.left}
                  x2={WIDTH - PADDING.right}
                  y1={y}
                  y2={y}
                  className="stroke-chart-grid"
                  strokeWidth="1"
                />
                <text
                  x={PADDING.left - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="central"
                  className="fill-ink-muted text-[11px] tabular-nums"
                >
                  {formatNumber(tick)}
                </text>
              </g>
            )
          })}

          {data.map((datum, index) => {
            const x = PADDING.left + index * band + (band - barWidth) / 2
            const barHeight = (datum.value / max) * plotHeight
            const topY = baselineY - barHeight
            const active = hovered === index

            return (
              <g key={datum.key} onMouseEnter={() => setHovered(index)}>
                {/* Full-height hit target: columns are too thin to hover. */}
                <rect
                  x={PADDING.left + index * band}
                  y={PADDING.top}
                  width={band}
                  height={plotHeight}
                  fill={active ? 'var(--app-surface-hover)' : 'transparent'}
                />
                <path
                  d={columnPath(x, topY, barWidth, baselineY)}
                  className="fill-series-1"
                  opacity={hovered === null || active ? 1 : 0.55}
                />
                {index % labelEvery === 0 ? (
                  <text
                    x={x + barWidth / 2}
                    y={baselineY + 14}
                    textAnchor="middle"
                    className="fill-ink-muted text-[11px] tabular-nums"
                  >
                    {datum.label}
                  </text>
                ) : null}
              </g>
            )
          })}

          {/* Selective direct label: the peak only. */}
          {peakIndex >= 0 && hovered === null ? (
            (() => {
              const datum = data[peakIndex]
              const x = PADDING.left + peakIndex * band + band / 2
              const topY = baselineY - (datum.value / max) * plotHeight
              return (
                <text
                  x={Math.min(Math.max(x, PADDING.left + 12), WIDTH - PADDING.right - 12)}
                  y={topY - 5}
                  textAnchor="middle"
                  className="fill-ink text-[11px] font-semibold tabular-nums"
                >
                  {formatNumber(datum.value)}
                </text>
              )
            })()
          ) : null}

          <line
            x1={PADDING.left}
            x2={WIDTH - PADDING.right}
            y1={baselineY}
            y2={baselineY}
            className="stroke-line-strong"
            strokeWidth="1"
          />
        </svg>

        {hovered !== null ? (
          <div
            className="pointer-events-none absolute -top-1 rounded-md border border-line bg-surface-raised px-2 py-1 text-xs shadow-md"
            style={{
              left: `${((hovered + 0.5) / data.length) * 100}%`,
              transform: 'translateX(-50%)',
            }}
            role="status"
          >
            <span className="font-medium text-ink">{data[hovered].label}</span>
            <span className="ml-1.5 tabular text-ink-secondary">
              {formatNumber(data[hovered].value)}
            </span>
          </div>
        ) : null}
      </div>
    </ChartFrame>
  )
}
